
import React, { useEffect, useRef, useState } from 'react';
import { Input } from "@/components/ui/input";
import { GOOGLE_MAPS_API_KEY } from '@/config/api';

interface AddressAutocompleteProps {
  onAddressSelect: (addressData: {
    address: string;
    street: string;
    street_number: string;
    city: string;
    zipCode: string;
    country: string;
  }) => void;
  defaultValue?: string;
  className?: string;
  onAddressChange?: (address: string) => void;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  onAddressSelect,
  defaultValue = '',
  className,
  onAddressChange,
}) => {
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const autocompleteElementRef = useRef<any>(null);
  const [inputValue, setInputValue] = useState(defaultValue || '');
  const [isAutocompleteAvailable, setIsAutocompleteAvailable] = useState(false);

  const handleAddressChange = (address: string) => {
    setInputValue(address);
    onAddressChange?.(address);
  };

  const handlePlaceSelect = async (event: Event) => {
    const placePrediction = (event as any).placePrediction;
    if (!placePrediction) return;

    const place = placePrediction.toPlace();
    await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });

    let streetNumber = '';
    let street = '';
    let city = '';
    let zipCode = '';
    let country = '';

    for (const component of place.addressComponents || []) {
      const types = component.types || [];
      const value = component.longText || component.shortText || '';

      if (types.includes('street_number')) streetNumber = value;
      else if (types.includes('route')) street = value;
      else if (types.includes('locality')) city = value;
      else if (types.includes('postal_code')) zipCode = value;
      else if (types.includes('country')) country = component.shortText || value;
    }

    const address = place.formattedAddress || '';
    handleAddressChange(address);
    onAddressSelect({ address, street, street_number: streetNumber, city, zipCode, country });
  };

  useEffect(() => {
    const scriptSelector = 'script[src^="https://maps.googleapis.com/maps/api/js"]';
    const existingScript = document.querySelector<HTMLScriptElement>(scriptSelector);
    const isPlaceApiAvailable = () => Boolean((window as any).google?.maps?.importLibrary);

    if (isPlaceApiAvailable()) {
      setIsAutocompleteAvailable(true);
      return;
    }

    let checkTimer: number | undefined;
    const handleScriptLoad = () => {
      checkTimer = window.setInterval(() => {
        if (isPlaceApiAvailable()) {
          window.clearInterval(checkTimer);
          setIsAutocompleteAvailable(true);
        }
      }, 100);
    };
    const handleScriptError = () => setIsAutocompleteAvailable(false);

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&loading=async&libraries=places&v=weekly`;
      script.async = true;
      script.defer = true;
      script.onload = handleScriptLoad;
      script.onerror = handleScriptError;
      document.head.appendChild(script);
    } else {
      existingScript.addEventListener('load', handleScriptLoad);
      existingScript.addEventListener('error', handleScriptError);
      handleScriptLoad();
    }

    return () => {
      if (checkTimer) window.clearInterval(checkTimer);
      existingScript?.removeEventListener('load', handleScriptLoad);
      existingScript?.removeEventListener('error', handleScriptError);
    };
  }, []);

  useEffect(() => {
    if (!isAutocompleteAvailable || !autocompleteRef.current || autocompleteElementRef.current) return;

    let cancelled = false;
    const setupAutocomplete = async () => {
      const placesLibrary = await (window as any).google.maps.importLibrary('places');
      if (cancelled || !autocompleteRef.current) return;

      const autocompleteElement = new placesLibrary.PlaceAutocompleteElement();
      autocompleteElement.includedPrimaryTypes = ['street_address'];
      autocompleteElement.placeholder = 'Enter your address';
      autocompleteElement.value = inputValue;
      autocompleteElement.className = 'w-full';
      autocompleteElementRef.current = autocompleteElement;
      autocompleteRef.current.replaceChildren(autocompleteElement);
      autocompleteElement.addEventListener('gmp-select', handlePlaceSelect);
      autocompleteElement.addEventListener('input', (event: Event) => {
        handleAddressChange((event.target as HTMLInputElement).value || '');
      });
    };

    setupAutocomplete().catch(() => setIsAutocompleteAvailable(false));

    return () => {
      cancelled = true;
      autocompleteElementRef.current?.removeEventListener('gmp-select', handlePlaceSelect);
      autocompleteElementRef.current = null;
    };
  }, [isAutocompleteAvailable]);

  return (
    <div className="relative w-full">
      {isAutocompleteAvailable ? (
        <div ref={autocompleteRef} className="w-full [&>gmp-place-autocomplete]:w-full" />
      ) : (
        <Input
          value={inputValue}
          onChange={(event) => handleAddressChange(event.target.value)}
          placeholder="Enter your address"
          className={className}
        />
      )}
    </div>
  );
};

export default AddressAutocomplete;
